import { useCallback, useRef } from 'react';

/** Subconjunto do Google Identity Services que usamos (initialize + renderButton). */
interface GoogleIdentityServices {
  accounts: {
    id: {
      initialize(config: {
        client_id: string;
        callback: (response: { credential?: string }) => void;
        auto_select?: boolean;
        cancel_on_tap_outside?: boolean;
      }): void;
      renderButton(
        parent: HTMLElement,
        options: {
          type?: 'standard' | 'icon';
          theme?: 'outline' | 'filled_blue' | 'filled_black';
          size?: 'small' | 'medium' | 'large';
          text?: 'signin_with' | 'signup_with' | 'continue_with';
          shape?: 'rectangular' | 'pill' | 'circle' | 'square';
          logo_alignment?: 'left' | 'center';
          width?: number;
          locale?: string;
        },
      ): void;
      cancel(): void;
    };
  };
}

declare global {
  interface Window {
    google?: GoogleIdentityServices;
  }
}

const SDK_ID = 'google-identity-services';
const SDK_SRC = 'https://accounts.google.com/gsi/client';

/**
 * Carrega o Google Identity Services sob demanda.
 *
 * Mesmo desenho do `useFacebookSdk`: injeta o `<script>` só no primeiro uso (fora do
 * boot), resolve quando `window.google` existe, e é idempotente — chamadas seguintes
 * reusam a mesma Promise. Falha limpa a Promise para permitir retry; sem isso, uma
 * queda de rede na primeira tentativa deixaria o botão morto até o F5.
 */
export function useGoogleSdk() {
  const loadingRef = useRef<Promise<GoogleIdentityServices> | null>(null);

  const load = useCallback((): Promise<GoogleIdentityServices> => {
    if (window.google?.accounts?.id) return Promise.resolve(window.google);
    if (loadingRef.current) return loadingRef.current;

    loadingRef.current = new Promise<GoogleIdentityServices>((resolve, reject) => {
      const finish = () => {
        if (!window.google?.accounts?.id) {
          loadingRef.current = null;
          reject(new Error('SDK do Google não inicializou.'));
          return;
        }
        resolve(window.google);
      };

      const existing = document.getElementById(SDK_ID);
      if (existing) {
        finish();
        return;
      }
      const script = document.createElement('script');
      script.id = SDK_ID;
      script.src = SDK_SRC;
      script.async = true;
      script.defer = true;
      script.onload = finish;
      script.onerror = () => {
        loadingRef.current = null; // permite retry numa próxima tentativa
        reject(new Error('Falha ao carregar o SDK do Google.'));
      };
      document.body.appendChild(script);
    });
    return loadingRef.current;
  }, []);

  return { load };
}
